/*
 * Marketing, Campaigns (moved over from the WordPress Steward, owner 2026-10-29): one list, each campaign a row saying
 * what it does, whether it is on and how it is doing; a row opens the campaign. A campaign's page reads top to bottom:
 * what it does and its switch, results, who gets it, what they get, how it stays safe, and the words behind Edit.
 */
import { Link } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";
import {
  CUSTOM_TEMPLATES,
  TEMPLATE_INFO,
  type CampaignRow,
  type OfferWho,
  type PcalPage,
  type PcalPreview,
  type PcalWave,
} from "@steward/shared";
import {
  useCampaigns,
  useCreateCustom,
  useOffer,
  usePcal,
  usePcalPreview,
  usePchk,
  useRenameCampaign,
  useRfu,
  useSaveOffer,
  useSavePcal,
  useSavePchk,
  useSaveRfu,
} from "../api/campaigns";
import { useSession } from "../app/session";
import { InputText } from "../components/fields";
import { shortDate } from "../components/format";
import { useToast } from "../components/overlays";
import { Banner, Button, Card, Skeleton, StatusLabel, Switch } from "../components/parts";
import { errText } from "../components/work";
import { CampaignLine } from "./MarketingTabs";
import { CustomCampaign } from "./CustomCampaign";

type Id = CampaignRow["id"];

export function Campaigns() {
  const { company, can } = useSession();
  const list = useCampaigns(company.id);
  const [open, setOpen] = useState<Id | null>(null);
  const [mine, setMine] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  if (!list.data) return <Skeleton rows={5} />;
  const rows = list.data.rows;
  const row = rows.find((r) => r.id === open);
  if (row) return <CampaignPage row={row} back={() => setOpen(null)} />;
  if (mine) return <CustomCampaign campaignKey={mine} back={() => setMine(null)} />;
  const custom = list.data.custom;
  return (
    <div className="stack">
      <Card title="Campaigns">
        <p className="muted">
          Texts that bring clients back and keep them booking. Each one runs by itself once it's on: weekdays during the day, a
          few at a time, and it stops for anyone who replies or opts out.
        </p>
      </Card>
      <Card flush>
        <ul className="auto-list">
          {rows.map((r) => (
            <li key={r.id}>
              <button type="button" className="setting-row camp-row" onClick={() => setOpen(r.id)}>
                <span className="what">
                  <b>{r.name}</b>
                  <span>{r.what}</span>
                  {r.line ? <span className="camp-line">{r.line}</span> : null}
                </span>
                <OnLabel on={r.on} paused={r.paused} />
              </button>
            </li>
          ))}
        </ul>
      </Card>
      <Card
        title="Your campaigns"
        action={
          can("marketing.update") && !picking ? (
            <Button size="small" icon="plus" onClick={() => setPicking(true)}>
              New campaign
            </Button>
          ) : null
        }
      >
        {picking ? (
          <NewCampaign
            done={(key) => {
              setPicking(false);
              if (key) setMine(key);
            }}
          />
        ) : custom.length === 0 ? (
          <p className="muted">
            Make your own. Pick who gets it by tags, work done, plans, visits or area, then lay out the texts and emails day by
            day.
          </p>
        ) : null}
      </Card>
      {custom.length ? (
        <Card flush>
          <ul className="auto-list">
            {custom.map((r) => (
              <li key={r.key}>
                <button type="button" className="setting-row camp-row" onClick={() => setMine(r.key)}>
                  <span className="what">
                    <b>{r.name}</b>
                    <span>{r.what}</span>
                    {r.line ? <span className="camp-line">{r.line}</span> : null}
                  </span>
                  <OnLabel on={r.on} />
                </button>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </div>
  );
}

/** New campaign: pick a starting point. It is made off and opens to be filled in. */
function NewCampaign({ done }: { done: (key: string | null) => void }) {
  const make = useCreateCustom();
  const toast = useToast();
  return (
    <div className="stack">
      <ul className="cc-templates">
        {CUSTOM_TEMPLATES.map((t) => (
          <li key={t}>
            <button
              type="button"
              disabled={make.isPending}
              onClick={() =>
                make.mutate(t, { onSuccess: (p) => done(p.key), onError: (e) => toast(errText(e), { kind: "error" }) })
              }
            >
              <b>{TEMPLATE_INFO[t].name}</b>
              <span className="muted">{TEMPLATE_INFO[t].what}</span>
            </button>
          </li>
        ))}
      </ul>
      <div>
        <Button type="tertiary" onClick={() => done(null)}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function OnLabel({ on, paused }: { on: boolean; paused?: boolean }) {
  if (paused) return <StatusLabel status="critical">Paused</StatusLabel>;
  return <StatusLabel status={on ? "success" : "inactive"}>{on ? "On" : "Off"}</StatusLabel>;
}

/** The campaign's name, editable: only the team sees it. Empty puts the Steward's name back. */
function NameEdit({ row, done }: { row: CampaignRow; done: () => void }) {
  const [name, setName] = useState(row.name);
  const save = useRenameCampaign();
  const toast = useToast();
  return (
    <Card title="Campaign name">
      <div className="stack">
        <InputText label="Campaign name" maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
        <p className="muted">Only you and your team see the name. Clients never do.</p>
        <div className="row">
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { id: row.id, name },
                {
                  onSuccess: () => {
                    toast("Name saved");
                    done();
                  },
                  onError: (e) => toast(errText(e), { kind: "error" }),
                },
              )
            }
          >
            Save name
          </Button>
          <Button type="tertiary" onClick={done}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}

function CampaignPage({ row, back }: { row: CampaignRow; back: () => void }) {
  const { can } = useSession();
  const [edit, setEdit] = useState(false);
  const owner = can("marketing.update");
  return (
    <div className="stack">
      <div className="row camp-bar">
        <Button type="tertiary" icon="arrowLeft" onClick={back}>
          All campaigns
        </Button>
        {owner && !edit ? (
          <Button type="secondary" size="small" icon="edit" onClick={() => setEdit(true)}>
            Edit name and texts
          </Button>
        ) : null}
      </div>
      {edit ? <NameEdit row={row} done={() => setEdit(false)} /> : null}
      {row.id === "pcal" ? (
        <Pcal name={row.name} edit={edit} done={() => setEdit(false)} />
      ) : row.id === "rfu" ? (
        <Rfu name={row.name} edit={edit} done={() => setEdit(false)} />
      ) : row.id === "pchk" ? (
        <Pchk name={row.name} edit={edit} done={() => setEdit(false)} />
      ) : row.id === "offer" ? (
        <Offer name={row.name} />
      ) : (
        <Referral name={row.name} on={row.on} line={row.line} />
      )}
    </div>
  );
}

/* ---------- shared bits ---------- */

function Head({
  name,
  what,
  on,
  paused,
  onChange,
  busy,
  children,
}: {
  name: string;
  what: string;
  on: boolean;
  paused?: string;
  onChange: (on: boolean) => void;
  busy: boolean;
  children?: ReactNode;
}) {
  const { can } = useSession();
  return (
    <Card title={name} action={<OnLabel on={on} paused={Boolean(paused)} />}>
      <div className="stack">
        <p className="muted">{what}</p>
        {children}
        <label className="row camp-switch">
          <Switch checked={on} disabled={!can("marketing.update") || busy} label="Send these automatically" onChange={onChange} />
          <span>Send these automatically</span>
        </label>
      </div>
    </Card>
  );
}

function Tiles({ items }: { items: [string, number | undefined][] }) {
  return (
    <div className="tiles">
      {items.map(([label, v]) => (
        <div key={label} className="tile">
          <span className="value">{v ?? 0}</span>
          <span className="label">{label}</span>
        </div>
      ))}
    </div>
  );
}

function Bubble({ kind, when, children }: { kind: "Text" | "Email"; when: string; children: ReactNode }) {
  return (
    <div className="camp-msg">
      <span className="camp-who">
        <StatusLabel status={kind === "Email" ? "informative" : "success"}>{kind}</StatusLabel>
        <span className="muted">{when}</span>
      </span>
      <div className="bubble">
        <p>{children}</p>
      </div>
    </div>
  );
}

function Safe({ lines }: { lines: string[] }) {
  return (
    <Card title="How it stays safe">
      <ul className="camp-safe">
        {lines.map((l) => (
          <li key={l}>{l}</li>
        ))}
      </ul>
    </Card>
  );
}

function Area({
  label,
  value,
  onChange,
  rows = 2,
  max,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  max: number;
}) {
  return (
    <label className="field wide">
      <span className="label">{label}</span>
      <textarea rows={rows} maxLength={max} value={value} onChange={(e) => onChange(e.target.value)} />
    </label>
  );
}

function useSaver<V>(mutate: (v: V, o: { onSuccess: () => void; onError: (e: unknown) => void }) => void) {
  const toast = useToast();
  return (v: V, msg: string, after?: () => void) =>
    mutate(v, { onSuccess: () => (toast(msg), after?.()), onError: (e) => toast(errText(e), { kind: "error" }) });
}

const day = (d: string) => shortDate(d);

/* ---------- Bring back past clients ---------- */

function Pcal({ name, edit, done }: { name: string; edit: boolean; done: () => void }) {
  const { company } = useSession();
  const q = usePcal(company.id);
  const save = useSavePcal();
  const run = useSaver(save.mutate);
  const preview = usePcalPreview();
  const [showMail, setShowMail] = useState(false);
  if (!q.data) return <Skeleton rows={6} />;
  const p = q.data;
  const st = p.stats;
  const wash = p.service === "window";
  const work = wash ? "service" : "cleaning";
  const often =
    p.gap === 90
      ? "About 4 texts a year."
      : p.gap === 60
        ? "About 6 texts a year, one every other month."
        : "One text a month, each with that month's reason to book.";
  const turn = (on: boolean) => {
    if (on && !confirm(`Turn on ${name}? It texts ${String(p.people)} past clients over the coming weeks.`)) return;
    run({ on }, on ? `${name} is on` : `${name} is off`);
  };
  return (
    <>
      <Head
        name={name}
        on={p.on}
        paused={p.paused}
        busy={save.isPending}
        onChange={turn}
        what={
          wash
            ? "A text every season to past window clients who haven't booked in a while, with that season's reason to book. The first text reminds them who you are and when you last came out."
            : "A text each month to past clients who haven't booked in a while, with that month's reason to book: the holidays, spring cleaning, back to school. The first text reminds them who you are and when you cleaned for them."
        }
      >
        {p.paused ? (
          <Banner
            type="error"
            action={
              <Button size="small" onClick={() => run({ resume: true }, "Resumed")}>
                Resume
              </Button>
            }
          >
            {p.paused}
          </Banner>
        ) : null}
      </Head>
      {edit ? <PcalEdit p={p} done={done} /> : null}
      <Card title="Results this month">
        <div className="stack">
          <p className="muted">
            This month's text is {p.now.name}, since {day(p.now.date)}.
            {p.next ? ` Next is ${p.next.name} on ${day(p.next.date)}.` : ""}
            {p.on ? ` ${String(p.sentToday)} sent today.` : ""}
          </p>
          <Tiles
            items={[
              ["texted", st.sent],
              ["still to text", p.left],
              ["replied", st.replied],
              ["booked", st.booked],
              ["opted out", st.stopped],
              ...(st.mailed ? ([["emailed", st.mailed]] as [string, number][]) : []),
            ]}
          />
          {p.bookedWho.length ? (
            <p>
              Booked after their text <b>{p.bookedWho.join(", ")}</b>
            </p>
          ) : null}
        </div>
      </Card>
      <Card title="Who gets it">
        <div className="stack">
          <p>
            {p.people} past client{p.people === 1 ? "" : "s"}
            {p.tag ? ` tagged ${p.tag}` : ""}
            {p.service ? ` who had ${p.service} work` : ""} whose last {work} was{" "}
            {p.sinceDays >= 180 ? "6 months" : `${String(p.sinceDays)} days`} or more ago and who have nothing booked, most recent
            first. {p.withEmail} of them also get an email 3 days later if they haven't replied.
            {p.comeback ? " Clients gone a year or more also get your comeback offer." : ""}
          </p>
          <p>
            <b>How often</b> {often}
          </p>
        </div>
      </Card>
      <UpNext p={p} skip={(id) => run({ skip: id }, "They won't get marketing texts")} />
      <Card title="What they get">
        <div className="stack">
          {p.sample ? (
            <>
              <Bubble kind="Text" when={`The first text ${p.sample.name} would get`}>
                {p.sample.first}
              </Bubble>
              <Bubble kind="Text" when="A later one">
                {p.sample.later}
              </Bubble>
            </>
          ) : (
            <p className="muted">Nobody is on the list right now.</p>
          )}
          <p className="muted">
            Most months also send an email 3 days after the text, only to the {p.withEmail} with an email who haven't replied.
          </p>
          <div className="row">
            <Button
              type="secondary"
              loading={preview.isPending}
              onClick={() => (preview.data ? preview.reset() : preview.mutate())}
            >
              {preview.data ? "Hide the months" : "See every month and who gets it"}
            </Button>
            <Button type="tertiary" onClick={() => setShowMail(!showMail)}>
              {showMail ? "Hide the email" : "See the email"}
            </Button>
          </div>
          {showMail ? <MailSample p={p} /> : null}
          {preview.data ? <Preview v={preview.data} /> : null}
        </div>
      </Card>
      <Safe
        lines={[
          `Weekdays 10 to 6, ${String(p.cap)} texts a day, most recent clients first.`,
          "Skips anyone with a complaint on file.",
          "Skips anyone who's texted with you in the last 14 days.",
          "A reply pauses them for 30 days so you or Eunice can answer.",
          "Asking to stop takes them off for good. They can still text you anytime.",
          "Nothing goes out until texts are live for this company.",
        ]}
      />
    </>
  );
}

function UpNext({ p, skip }: { p: PcalPage; skip: (id: string) => void }) {
  const { can } = useSession();
  if (!p.upNext.length) return null;
  return (
    <Card title="Up next" flush>
      <p className="muted auto-intro">
        The next {p.upNext.length} to get this month's text, in order. Take out anyone who left unhappy. They won't get marketing
        texts again, but still get invoices and reminders. Anyone with a complaint on file is already left out.
      </p>
      <table className="table camp-table">
        <thead>
          <tr>
            <th>Client</th>
            <th>Last {p.service === "window" ? "service" : "cleaning"}</th>
            <th>With you</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {p.upNext.map((u) => (
            <tr key={u.id}>
              <td>
                <Link to="/clients/$id" params={{ id: u.id }}>
                  {u.name}
                </Link>
                {u.texted ? <div className="muted">Texted before</div> : null}
              </td>
              <td>{day(u.last)}</td>
              <td>{u.jobs ? `${String(u.jobs)} visit${u.jobs === 1 ? "" : "s"}` : ""}</td>
              <td>
                {can("marketing.update") ? (
                  <Button type="tertiary" size="small" onClick={() => skip(u.id)}>
                    Don't text
                  </Button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function MailSample({ p }: { p: PcalPage }) {
  const w = p.waves.find((x) => x.id === p.now.id && x.mail) ?? p.waves.find((x) => x.mail);
  if (!w || !p.sample) return <p className="muted">No email to show right now.</p>;
  return (
    <div className="camp-mail">
      <span className="muted">The {w.name} email</span>
      <b>{w.subject}</b>
      <p>
        {p.sample.first.replace(/ Reply STOP to opt out\.$/, "")}
        {"\n\n"}Tap below to pick a day, or just reply to this email.
      </p>
      <span className="btn primary small" aria-hidden>
        Pick a day
      </span>
    </div>
  );
}

function Preview({ v }: { v: PcalPreview }) {
  return (
    <div className="stack camp-preview">
      <b>The year ahead for {v.for || "one client"}</b>
      <p className="muted">
        {v.gap === 25 ? "One text a month" : `One text every ${String(v.gap)} days`}, each with that month's words. Most months
        also have an email 3 days after the text.
      </p>
      {v.year.length === 0 ? <p className="muted">Nobody qualifies right now.</p> : null}
      {v.year.map((y, i) => (
        <div key={y.name} className="stack">
          <b>
            {i === 0 ? "First, " : "Then "}
            {y.name}
          </b>
          <Bubble kind="Text" when={`around ${day(y.date)}`}>
            {y.text}
          </Bubble>
          {y.mail ? (
            <Bubble kind="Email" when={`around ${day(y.mail.date)}, if they have an email and haven't replied`}>
              <b>{y.mail.subject}</b>
              {"\n"}
              {y.mail.text}
            </Bubble>
          ) : null}
        </div>
      ))}
      <b>
        Everyone in {v.season}: {v.people.length}
      </b>
      <p className="muted">The exact first text each one would get, most recent clients first.</p>
      {v.people.length ? (
        <table className="table camp-table">
          <thead>
            <tr>
              <th>Client</th>
              <th>Last cleaning</th>
              <th>Text</th>
              <th>Email</th>
            </tr>
          </thead>
          <tbody>
            {v.people.map((x) => (
              <tr key={x.name + x.phone} className={x.got ? "muted" : ""}>
                <td>
                  <b>{x.name}</b>
                  <div className="muted">{x.phone}</div>
                </td>
                <td>{day(x.last)}</td>
                <td>
                  {x.text}
                  {x.got ? " (already sent)" : ""}
                </td>
                <td>{x.email ? "Yes" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

function PcalEdit({ p, done }: { p: PcalPage; done: () => void }) {
  const save = useSavePcal();
  const run = useSaver(save.mutate);
  const [f, setF] = useState({
    gap: p.gap as 25 | 60 | 90,
    hello1: p.hello1,
    hello2: p.hello2,
    comeback: p.comeback,
    optoutReply: p.optoutReply,
    waves: p.waves.map((w) => ({ ...w })),
  });
  const wave = (id: string, patch: Partial<PcalWave>) =>
    setF({ ...f, waves: f.waves.map((w) => (w.id === id ? { ...w, ...patch } : w)) });
  const wash = p.service === "window";
  return (
    <Card title="Texts">
      <div className="stack">
        <label className="field">
          <span className="label">How often each person gets a text</span>
          <select value={f.gap} onChange={(e) => setF({ ...f, gap: Number(e.target.value) as 25 | 60 | 90 })}>
            <option value={25}>Once a month</option>
            <option value={60}>Every 60 days</option>
            <option value={90}>Every 90 days (quarterly)</option>
          </select>
        </label>
        <InputText
          label="First hello"
          maxLength={200}
          value={f.hello1}
          onChange={(e) => setF({ ...f, hello1: e.target.value })}
        />
        <InputText
          label="Hello on every text after the first"
          maxLength={200}
          value={f.hello2}
          onChange={(e) => setF({ ...f, hello2: e.target.value })}
        />
        <Area
          label={`Comeback offer, for clients whose last ${wash ? "service" : "cleaning"} was a year or more ago (leave empty for none)`}
          max={200}
          value={f.comeback}
          onChange={(v) => setF({ ...f, comeback: v })}
        />
        <Area
          label="When someone asks to stop, they get this once"
          max={300}
          value={f.optoutReply}
          onChange={(v) => setF({ ...f, optoutReply: v })}
        />
        <p className="muted">
          Fill-ins {"{first} {business} {did} {when}"}. {"{did}"} names their last service, like "did your deep cleaning" or
          "cleaned your home". {"{when}"} is "in March", or "in 2024" when it was over a year ago. Dates that move each year{" "}
          {"{mardigras} {mothersday} {thanksgiving}"}. Say what they get, lead with the whole house, and end with one question.
        </p>
        {f.waves.map((w) => {
          const len = f.hello2.replace("{first}", "Sarah").replace("{business}", "The Cleaners").length + 1 + w.text.length;
          return (
            <fieldset key={w.id} className="stack auto-step">
              <b>
                {w.name}{" "}
                <span className="muted">
                  from {day(`2026-${w.start}`)}. {len} characters with the hello
                  {len > 160 ? ", over one text (160)" : ", one text"}
                </span>
              </b>
              <Area
                label="First text (someone never texted before)"
                max={400}
                value={w.first}
                onChange={(v) => wave(w.id, { first: v })}
              />
              <Area label="Every text after (short)" max={400} value={w.text} onChange={(v) => wave(w.id, { text: v })} />
              {w.mail ? (
                <InputText
                  label="Email 3 days after the text, its subject"
                  maxLength={120}
                  value={w.subject}
                  onChange={(e) => wave(w.id, { subject: e.target.value })}
                />
              ) : (
                <span className="muted">No email this month, the text only.</span>
              )}
            </fieldset>
          );
        })}
        <div className="row end">
          <Button type="tertiary" onClick={done}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            onClick={() =>
              run({ ...f, waves: f.waves.map(({ id, text, first, subject }) => ({ id, text, first, subject })) }, "Saved", done)
            }
          >
            Save
          </Button>
        </div>
      </div>
    </Card>
  );
}

/* ---------- Turn first-timers into regulars ---------- */

function Rfu({ name, edit, done }: { name: string; edit: boolean; done: () => void }) {
  const { company } = useSession();
  const q = useRfu(company.id);
  const save = useSaveRfu();
  const run = useSaver(save.mutate);
  const [f, setF] = useState<{
    hello: string;
    hello2: string;
    steps: { days: number; text: string; plain: string }[];
  } | null>(null);
  if (!q.data) return <Skeleton rows={6} />;
  const r = q.data;
  const form = f ?? { hello: r.hello, hello2: r.hello2, steps: r.steps.map((s) => ({ ...s })) };
  return (
    <>
      <Head
        name={name}
        on={r.on}
        busy={save.isPending}
        onChange={(on) => run({ on }, on ? `${name} is on` : `${name} is off`)}
        what="After a client's first cleaning, if it was a one-time booking, they get a few texts offering weekly, every 2 weeks or monthly at their own price from your price list."
      />
      {edit ? (
        <Card title="Texts">
          <div className="stack">
            <InputText
              label="Hello, if we never texted them our name"
              maxLength={120}
              value={form.hello}
              onChange={(e) => setF({ ...form, hello: e.target.value })}
            />
            <InputText
              label="Hello, once they know it's us"
              maxLength={120}
              value={form.hello2}
              onChange={(e) => setF({ ...form, hello2: e.target.value })}
            />
            {form.steps.map((s, i) => (
              <fieldset key={i} className="stack auto-step">
                <InputText
                  label="Day after their cleaning"
                  type="number"
                  min={1}
                  max={59}
                  value={String(s.days)}
                  onChange={(e) =>
                    setF({
                      ...form,
                      steps: form.steps.map((x, j) => (j === i ? { ...x, days: Number(e.target.value) || 1 } : x)),
                    })
                  }
                />
                <Area
                  label="With their price ({weekly}, {biweekly} or {monthly}, from your price list)"
                  max={300}
                  value={s.text}
                  onChange={(v) => setF({ ...form, steps: form.steps.map((x, j) => (j === i ? { ...x, text: v } : x)) })}
                />
                <Area
                  label="When their price can't be worked out"
                  max={300}
                  value={s.plain}
                  onChange={(v) => setF({ ...form, steps: form.steps.map((x, j) => (j === i ? { ...x, plain: v } : x)) })}
                />
              </fieldset>
            ))}
            <div className="row end">
              <Button
                type="tertiary"
                onClick={() => {
                  setF(null);
                  done();
                }}
              >
                Cancel
              </Button>
              <Button
                loading={save.isPending}
                onClick={() =>
                  run(form, "Saved", () => {
                    setF(null);
                    done();
                  })
                }
              >
                Save
              </Button>
            </div>
          </div>
        </Card>
      ) : null}
      <Card title="Results">
        <Tiles
          items={[
            ["in their follow-up now", r.now],
            ["texted", r.stats.sent],
            ["replied", r.stats.replied],
            [`of ${String(r.followed)} went recurring or booked again`, r.converted],
            ["opted out", r.stats.stopped],
          ]}
        />
      </Card>
      <Card title="Who gets it">
        <p>
          Home clients whose first cleaning was a one-time booking of a service with recurring prices on your price list (so not
          an Airbnb turnover). Only cleanings from the day you switch it on count. Clients already on a schedule, businesses and
          repeat clients are left out.
        </p>
      </Card>
      <Card title="What they get">
        <div className="stack">
          <p className="muted">Texts only, no emails.</p>
          {r.steps.map((s, i) => (
            <Bubble key={i} kind="Text" when={`Day ${String(s.days)} after their cleaning`}>
              {r.samples[i]}
            </Bubble>
          ))}
          <p className="muted">
            Prices shown for a sample home. Each client sees their own, from the price list answers on their job or from the price
            of their first cleaning. If their price can't be worked out, the text leaves it out. Your business name and the
            opt-out line are added only if none of your texts to them had them yet.
          </p>
        </div>
      </Card>
      <Safe
        lines={[
          "Weekdays 10 to 6, within the same daily limit as your other campaigns.",
          "Waits while the invoice for that cleaning is unpaid, and for a day after a review request.",
          'Stops when they reply (a "no thanks" too), book again, go on a schedule or opt out.',
          'After 60 days, "Bring back past clients" takes over, so nobody gets both.',
        ]}
      />
    </>
  );
}

/* ---------- Plan check-ins ---------- */

function Pchk({ name, edit, done }: { name: string; edit: boolean; done: () => void }) {
  const { company } = useSession();
  const q = usePchk(company.id);
  const save = useSavePchk();
  const run = useSaver(save.mutate);
  const [steps, setSteps] = useState<string[] | null>(null);
  if (!q.data) return <Skeleton rows={5} />;
  const k = q.data;
  const form = steps ?? k.steps.map((s) => s.text);
  const nth = (n: number) => (n === 2 ? "2nd" : "3rd");
  return (
    <>
      <Head
        name={name}
        on={k.on}
        busy={save.isPending}
        onChange={(on) => run({ on }, on ? `${name} is on` : `${name} is off`)}
        what="Most plans that end, end in the first few months. After a new plan's 2nd and 3rd visit, the client gets one short text asking how it went. A reply comes to your Inbox, so you can fix it before they cancel."
      />
      {edit ? (
        <Card title="Texts">
          <div className="stack">
            {k.steps.map((s, i) => (
              <Area
                key={s.n}
                label={`After their ${nth(s.n)} visit`}
                rows={3}
                max={300}
                value={form[i] ?? ""}
                onChange={(v) => setSteps(form.map((x, j) => (j === i ? v : x)))}
              />
            ))}
            <p className="muted">Fill-ins {"{first} {business}"}</p>
            <div className="row end">
              <Button
                type="tertiary"
                onClick={() => {
                  setSteps(null);
                  done();
                }}
              >
                Cancel
              </Button>
              <Button
                loading={save.isPending}
                onClick={() =>
                  run({ steps: form.map((text) => ({ text })) }, "Saved", () => {
                    setSteps(null);
                    done();
                  })
                }
              >
                Save
              </Button>
            </div>
          </div>
        </Card>
      ) : null}
      <Card title="Results">
        <Tiles
          items={[
            ["new plans in it now", k.stats.now],
            ["texted", k.stats.sent],
            ["replied", k.stats.replied],
          ]}
        />
      </Card>
      <Card title="Who gets it">
        <p>
          Clients whose plan starts on or after the day you switch this on. Plans already going are left alone. Clients set to no
          marketing texts don't get it.
        </p>
      </Card>
      <Card title="What they get">
        <div className="stack">
          <p className="muted">Texts only. Shown for a sample client, Sarah.</p>
          {k.steps.map((s, i) => (
            <Bubble key={s.n} kind="Text" when={`2 hours or more after their ${nth(s.n)} visit is marked done`}>
              {k.samples[i]}
            </Bubble>
          ))}
        </div>
      </Card>
      <Safe
        lines={[
          "Monday to Saturday, 10 to 6.",
          "Waits a day after a review request, and while they texted you in the last day.",
          "Once each. If both are due, only the 3rd goes. A visit more than 5 days back is not asked about.",
          "Asking to stop takes them off marketing texts.",
        ]}
      />
    </>
  );
}

/* ---------- One-time offer ---------- */

const MONTHS = [0, 3, 6, 12, 24];

function Offer({ name }: { name: string }) {
  const { company, can } = useSession();
  const q = useOffer(company.id);
  const save = useSaveOffer();
  const run = useSaver(save.mutate);
  const [f, setF] = useState<{ date: string; text: string; who: OfferWho } | null>(null);
  if (!q.data) return <Skeleton rows={4} />;
  const o = q.data;
  const form = f ?? { date: o.date, text: o.text, who: o.who };
  const owner = can("marketing.update");
  return (
    <>
      <Head
        name={name}
        on={o.on}
        busy={save.isPending}
        onChange={(on) => {
          if (on && !confirm(`Send this to ${String(o.count)} clients from ${o.date || "the day you picked"}?`)) return;
          run({ on }, on ? `${name} is on` : `${name} is off`);
        }}
        what="One text to active clients with a mobile number, once, starting on the date you pick. Send it to every client, or by the services they have had. Change the text or the date to send a new one."
      />
      <Card title="The offer">
        <div className="stack">
          <InputText
            label="Start sending on"
            type="date"
            value={form.date}
            disabled={!owner}
            onChange={(e) => setF({ ...form, date: e.target.value })}
          />
          <Area
            label="Text (fill-ins {first} {name})"
            rows={3}
            max={600}
            value={form.text}
            onChange={(v) => setF({ ...form, text: v })}
          />
          <div className="row wrap">
            <label className="field">
              <span className="label">Send to</span>
              <select
                value={form.who.mode}
                onChange={(e) => setF({ ...form, who: { ...form.who, mode: e.target.value as OfferWho["mode"] } })}
              >
                <option value="all">Every client</option>
                <option value="had">Clients who had</option>
                <option value="never">Clients who never had</option>
              </select>
            </label>
            {form.who.mode !== "all" ? (
              <label className="field">
                <span className="label">Kind of work</span>
                <select
                  value={form.who.service}
                  onChange={(e) => setF({ ...form, who: { ...form.who, service: e.target.value } })}
                >
                  <option value="">Pick one</option>
                  {o.services.map((s) => (
                    <option key={s}>{s}</option>
                  ))}
                </select>
              </label>
            ) : null}
            {form.who.mode === "had" ? (
              <label className="field">
                <span className="label">Last done</span>
                <select
                  value={form.who.months}
                  onChange={(e) => setF({ ...form, who: { ...form.who, months: Number(e.target.value) } })}
                >
                  {MONTHS.map((m) => (
                    <option key={m} value={m}>
                      {m === 0 ? "Any time" : `Over ${String(m)} months ago`}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
          <p className="muted">
            {o.count} active client{o.count === 1 ? "" : "s"} with a mobile number get it now.{" "}
            {o.sent ? `${String(o.sent)} got it so far.` : ""}
          </p>
          {owner ? (
            <div className="row end">
              <Button loading={save.isPending} disabled={!f} onClick={() => run(form, "Saved", () => setF(null))}>
                Save
              </Button>
            </div>
          ) : null}
        </div>
      </Card>
      <Safe
        lines={[
          "9 AM to 7 PM, 15 an hour.",
          "Each client gets it once. Changing only who gets it never sends it twice.",
          "Clients set to no marketing texts don't get it.",
        ]}
      />
    </>
  );
}

/* ---------- Referrals ---------- */

function Referral({ name, on, line }: { name: string; on: boolean; line: string }) {
  return (
    <Card title={name} action={<OnLabel on={on} />}>
      <div className="stack">
        <p className="muted">
          A text with their own link after they tap your review link. Their friend gets money off, and so do they.
        </p>
        {line ? <p>{line}</p> : null}
        <p>
          Its words and switch are with the automatic messages.{" "}
          <Link to="/settings/automations/$kind" params={{ kind: "referral_offer" }}>
            Open Referral offer
          </Link>
        </p>
      </div>
    </Card>
  );
}

/** The campaigns' lines for the Marketing Dashboard's Campaigns card: each one's name, numbers and on or off. */
export function CampaignLines({ onOpen }: { onOpen: () => void }) {
  const { company } = useSession();
  const list = useCampaigns(company.id);
  if (!list.data) return null;
  return (
    <>
      {list.data.rows
        .filter((r) => r.id !== "referral")
        .map((r) => (
          <CampaignLine key={r.id} name={r.name} line={r.line || r.what} on={r.on} onOpen={onOpen} />
        ))}
      {list.data.custom.map((r) => (
        <CampaignLine key={r.key} name={r.name} line={r.line || r.what} on={r.on} onOpen={onOpen} />
      ))}
    </>
  );
}
